/**
 * RangeBarsChart Component
 *
 * Horizontal bar chart showing experience ranges.
 * Each bar shows start-end period with fill indicating duration.
 */

import './charts.css'

interface RangeEntry {
  name: string
  start: number
  end: number
  color: string
  years?: number
}

interface RangeBarsChartProps {
  entries: RangeEntry[]
  startYear: number
  endYear: number
  title?: string
}

export function RangeBarsChart({
  entries,
  startYear,
  endYear,
  title,
}: RangeBarsChartProps) {
  const totalYears = endYear - startYear

  // Scale function: year to percentage
  const yearToPercent = (year: number) => {
    return ((year - startYear) / totalYears) * 100
  }

  return (
    <div className="range-bars-chart">
      {title && <h3 className="chart-title">{title}</h3>}

      <div className="range-bars-chart__container">
        {/* Year axis */}
        <div className="range-bars-chart__axis">
          <span>{startYear}</span>
          <span>{Math.round(startYear + totalYears * 0.25)}</span>
          <span>{Math.round(startYear + totalYears * 0.5)}</span>
          <span>{Math.round(startYear + totalYears * 0.75)}</span>
          <span>{endYear}</span>
        </div>

        {/* Bars */}
        <div className="range-bars-chart__bars">
          {entries.map((entry) => {
            const startPercent = yearToPercent(entry.start)
            const endPercent = yearToPercent(entry.end)
            const widthPercent = endPercent - startPercent
            const years = entry.years ?? entry.end - entry.start

            return (
              <div key={entry.name} className="range-bars-chart__row">
                <div className="range-bars-chart__label">
                  {entry.name}
                </div>
                <div className="range-bars-chart__track">
                  <div
                    className="range-bars-chart__fill"
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      backgroundColor: entry.color,
                    }}
                    title={`${entry.name}: ${entry.start} - ${entry.end} (${years} years)`}
                  >
                    {widthPercent > 15 && (
                      <span className="range-bars-chart__value">
                        {years}yr
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RangeBarsChart
